import { defineStore } from 'pinia'

export const useAuthStore = defineStore('authStore', () => {
  const { BFF_ENDPOINTS } = useApiEndpoints()
  const { data: session, status, signIn, signOut, getSession } = useAuth()

  const addressStore = useUserAddressStore()
  const cartStore = useCartStore()

  const user = ref({
    email: '',
    password: ''
  })

  const register = ref({
    first_name: '',
    surname: '',
    email: '',
    password: '',
    password_confirmation: '',
    vendor_token: '',
    store_name: ''
  })

  const apiError = ref({
    login: [],
    register: [],
    remind: []
  })

  const loading = ref({
    login: false,
    register: false,
    remind: false
  })

  const _anon = ref({
    id: null,
    name: null
  })

  const anon = computed({
    get: () => _anon.value,
    set: (value) => {
      _anon.value = value
      if (process.client && value?.id) {
        document.cookie = `anon_user_id=${value.id}; path=/; max-age=${30 * 24 * 60 * 60};`
      }
    }
  })

  const userInfo = computed(() => session.value?.user ?? null)
  const isAuthenticated = computed(() => status.value === 'authenticated' && Boolean(userInfo.value?.id || userInfo.value?.email))

  const actionsOnLogin = async () => {
    await addressStore.fetchAddresses()
    await cartStore.fetchCartItems()
  }

  const actionsOnLogout = async () => {
    await addressStore.fetchAddresses()
    await cartStore.fetchCartItems()
  }

  const initFromSession = async () => {
    if (process.client) {
      try {
        await getSession()
      } catch (_) {}

      const anonId = document.cookie.match(/anon_user_id=([^;]+)/)?.[1]
      if (anonId && !_anon.value.id) {
        _anon.value.id = anonId
      }
    }
  }

  if (process.client) {
    initFromSession()
  }

  const login = async (options = {}) => {
    loading.value.login = true
    apiError.value.login = []
    const { requiredRole = null, loginEndpoint = null } = options

    const payload = {
      email: user.value.email,
      password: user.value.password,
      anonymousId: anon.value?.id,
      required_role: requiredRole || undefined,
      login_endpoint: loginEndpoint || undefined
    }

    const response = await signIn('credentials', {
      ...payload,
      redirect: false
    })

    loading.value.login = false

    if (response?.error) {
      apiError.value.login = [[response.error]]
      return null
    }

    await getSession({ forceRefresh: true })
    await actionsOnLogin()
    return response
  }

  const logout = async (callback = null) => {
    try {
      await $fetch(BFF_ENDPOINTS.auth.logout, { method: 'POST' })
    } catch (_) {}

    await signOut({ redirect: false })
    await actionsOnLogout()
    if (callback) {
      navigateTo(callback)
    } else {
      navigateTo('/auth')
    }
  }

  const registerUser = async () => {
    loading.value.register = true
    apiError.value.register = []

    try {
      const response = await $fetch(BFF_ENDPOINTS.auth.register, {
        method: 'POST',
        body: { ...register.value }
      })

      if (response?.token) {
        const signInResult = await signIn('credentials', {
          email: register.value.email,
          password: register.value.password,
          anonymousId: anon.value?.id,
          required_role: 'user',
          login_endpoint: '/auth/login/user',
          redirect: false
        })
        if (signInResult?.error) {
          apiError.value.register = [[signInResult.error]]
          return false
        }
        await getSession({ forceRefresh: true })
        await actionsOnLogin()
        return true
      }

      return false
    } catch (error) {
      const data = error?.data || error?.response?._data
      apiError.value.register = Array.isArray(data) ? data : [[data?.message || 'Kayıt başarısız']]
      return false
    } finally {
      loading.value.register = false
    }
  }

  const registerVendor = async () => {
    loading.value.register = true
    apiError.value.register = []

    const endpoint = register.value.vendor_token ? BFF_ENDPOINTS.auth.registerVendor : BFF_ENDPOINTS.auth.register

    try {
      const response = await $fetch(endpoint, {
        method: 'POST',
        body: { ...register.value }
      })

      if (response?.token) {
        const signInResult = await signIn('credentials', {
          email: register.value.email,
          password: register.value.password,
          anonymousId: anon.value?.id,
          redirect: false
        })
        if (signInResult?.error) {
          apiError.value.register = [[signInResult.error]]
          return false
        }
        await getSession({ forceRefresh: true })
        await actionsOnLogin()
        return true
      }

      return false
    } catch (error) {
      const data = error?.data || error?.response?._data
      apiError.value.register = Array.isArray(data) ? data : [[data?.message || 'Kayıt başarısız']]
      return false
    } finally {
      loading.value.register = false
    }
  }

  const remind = async (email) => {
    loading.value.remind = true
    apiError.value.remind = []

    try {
      await $fetch(BFF_ENDPOINTS.auth.remind, {
        method: 'POST',
        body: { email }
      })
    } catch (error) {
      const data = error?.data || error?.response?._data
      if (Array.isArray(data)) {
        apiError.value.remind = data
      } else if (data?.message) {
        apiError.value.remind = [[data.message]]
      }
    } finally {
      loading.value.remind = false
    }
  }

  const changePassword = async (form) => {
    loading.value.remind = true
    try {
      return await $fetch(BFF_ENDPOINTS.auth.resetPassword, {
        method: 'POST',
        body: { ...form }
      })
    } finally {
      loading.value.remind = false
    }
  }

  return {
    user,
    register,
    anon,
    apiError,
    loading,
    userInfo,
    isAuthenticated,
    login,
    logout,
    registerUser,
    registerVendor,
    remind,
    changePassword,
    initFromSession
  }
})
